// Accounts (IMPLEMENTATION_PLAN.md §17, §18.4).
//
// Three things this page deliberately does not do:
//
//   1. Delete. Deactivation is offered instead, because a delete would cascade someone's boards
//      and images away, and "this person has left" is not "destroy their work" (§17.2).
//   2. Duplicate the anti-lockout rules. The server refuses self-demotion and demoting the last
//      admin, and returns a user-safe title with the reason; that message is shown verbatim. A
//      second copy of the rules here could only ever disagree with the one that is enforced.
//   3. Edit the allowlist. `Auth__AllowedEmails` is configuration, outside the database, and that
//      is what makes it a recovery path.

import { useCallback, useEffect, useState } from "react";
import type { ReactElement } from "react";
import { UserPlus } from "lucide-react";
import { api, formatDate, formatRelative } from "../api";
import type { ManagedUser, UserRole } from "../api";
import {
  AsyncButton,
  ConfirmModal,
  EmptyState,
  ErrorNote,
  Modal,
  Skeleton,
  describeError,
} from "../components/ui";

const ALLOWLIST_REASON =
  "This address is set in Auth__AllowedEmails. Its role and status are managed there.";

export function AdminPage(): ReactElement {
  const [users, setUsers] = useState<ManagedUser[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [deactivating, setDeactivating] = useState<ManagedUser | null>(null);
  const [dialogError, setDialogError] = useState<unknown>(null);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    setUsers(await api.listUsers(signal));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setError(cause);
      setUsers([]);
    });
    return () => controller.abort();
  }, [load]);

  async function changeRole(user: ManagedUser, role: UserRole): Promise<void> {
    if (role === user.role) return;
    setError(null);
    setPendingId(user.id);
    try {
      await api.updateUser(user.id, { role });
      await load();
    } catch (cause) {
      // Includes the anti-lockout refusals (§17.1). The server's wording is the wording.
      setError(cause);
    } finally {
      setPendingId(null);
    }
  }

  async function setActive(user: ManagedUser, isActive: boolean): Promise<void> {
    setError(null);
    setDialogError(null);
    setPendingId(user.id);
    try {
      await api.updateUser(user.id, { is_active: isActive });
      await load();
      setDeactivating(null);
    } catch (cause) {
      // Shown inside the confirmation when there is one, so the refusal lands where the action did.
      if (isActive) setError(cause);
      else setDialogError(cause);
    } finally {
      setPendingId(null);
    }
  }

  const failure = error ? describeError(error) : null;
  const dialogFailure = dialogError ? describeError(dialogError) : null;

  return (
    <div className="page">
      <div className="page-inner">
        <div className="page-header">
          <div>
            <h1 className="page-title">People</h1>
            <p className="lede">
              Who can sign in, and what they can do. Admins can also see spend for everyone.
            </p>
          </div>
          <button
            type="button"
            className="btn primary"
            onClick={() => {
              setError(null);
              setAdding(true);
            }}
          >
            <UserPlus size={14} aria-hidden />
            Add person
          </button>
        </div>

        {failure && (
          <div className="col" style={{ marginBottom: "var(--space-5)" }}>
            <ErrorNote title={failure.title} detail={failure.detail} />
          </div>
        )}

        {users === null && (
          <div className="card">
            <div className="col">
              {[0, 1, 2, 3].map((index) => (
                <Skeleton key={index} height={40} />
              ))}
            </div>
          </div>
        )}

        {users !== null && users.length === 0 && !failure && (
          <EmptyState
            title="No accounts yet."
            hint="Add someone by email. They can sign in with Google as soon as the account exists."
            action={
              <button type="button" className="btn primary" onClick={() => setAdding(true)}>
                Add person
              </button>
            }
          />
        )}

        {users !== null && users.length > 0 && (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col">Role</th>
                  <th scope="col">Status</th>
                  <th scope="col">Created</th>
                  <th scope="col">Last seen</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const locked = user.is_from_allowlist;
                  const busy = pendingId === user.id;

                  return (
                    <tr key={user.id}>
                      <td>
                        <span>{user.display_name || user.email}</span>
                        <span className="stat-note" style={{ display: "block" }}>
                          {user.email}
                        </span>
                      </td>

                      <td>
                        <select
                          className="select"
                          value={user.role}
                          disabled={locked || busy}
                          title={locked ? ALLOWLIST_REASON : undefined}
                          aria-label={`Role for ${user.email}`}
                          onChange={(event) => {
                            void changeRole(user, event.target.value as UserRole);
                          }}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                        {locked && (
                          <span className="stat-note" style={{ display: "block" }}>
                            Always an admin. Set in Auth__AllowedEmails, and the way back in if
                            nobody in the database can sign in.
                          </span>
                        )}
                      </td>

                      <td>
                        {user.is_active ? (
                          <span className="muted">Active</span>
                        ) : (
                          <span className="chip warn">Deactivated</span>
                        )}
                      </td>

                      <td className="muted">{formatDate(user.created_at)}</td>
                      <td className="muted">{formatRelative(user.last_seen_at)}</td>

                      <td>
                        {user.is_active ? (
                          <button
                            type="button"
                            className="btn ghost danger"
                            disabled={locked || busy}
                            title={locked ? ALLOWLIST_REASON : undefined}
                            onClick={() => {
                              setDialogError(null);
                              setDeactivating(user);
                            }}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <AsyncButton
                            className="btn ghost"
                            disabled={locked || busy}
                            disabledReason={locked ? ALLOWLIST_REASON : undefined}
                            onClick={() => setActive(user, true)}
                          >
                            Activate
                          </AsyncButton>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adding && (
        <AddPerson
          onClose={() => setAdding(false)}
          onAdded={async () => {
            // The account exists by now, so a failure to re-read the list is a separate problem
            // and is reported as one rather than as a failed create.
            setAdding(false);
            try {
              await load();
            } catch (cause) {
              setError(cause);
            }
          }}
        />
      )}

      {deactivating && (
        <ConfirmModal
          danger
          title={`Deactivate ${deactivating.email}?`}
          body={
            <>
              {dialogFailure && (
                <ErrorNote title={dialogFailure.title} detail={dialogFailure.detail} />
              )}
              <p>
                They lose access at their next request, within five minutes. Their boards, frames
                and images are kept, and nothing they made is deleted or transferred.
              </p>
              <p className="field-help">
                You can activate them again at any time, and they pick up where they left off.
              </p>
            </>
          }
          confirmLabel="Deactivate"
          onConfirm={() => setActive(deactivating, false)}
          onCancel={() => {
            setDialogError(null);
            setDeactivating(null);
          }}
        />
      )}
    </div>
  );
}

// ── Add person ─────────────────────────────────────────────────────────────────────────────

function AddPerson({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: () => Promise<void>;
}): ReactElement {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<UserRole>("user");
  const [error, setError] = useState<unknown>(null);

  async function submit(): Promise<void> {
    setError(null);
    try {
      await api.createUser(email.trim(), role);
      await onAdded();
    } catch (cause) {
      // The server owns what counts as a valid or duplicate address, so its message is shown as is.
      setError(cause);
    }
  }

  const failure = error ? describeError(error) : null;
  const ready = email.trim().length > 0;

  return (
    <Modal
      title="Add person"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <AsyncButton
            className="btn primary"
            disabled={!ready}
            disabledReason="Enter an email address first."
            onClick={submit}
          >
            Add person
          </AsyncButton>
        </>
      }
    >
      {failure && <ErrorNote title={failure.title} detail={failure.detail} />}

      <label className="field">
        <span className="field-label">Email address</span>
        <input
          className="input"
          type="email"
          autoComplete="off"
          autoFocus
          placeholder="name@example.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <span className="field-help">
          The Google account they sign in with. The account is created now and is ready the first
          time they sign in.
        </span>
      </label>

      <div className="field">
        <span className="field-label">Role</span>
        <div className="segmented" role="group" aria-label="Role">
          <button
            type="button"
            className={role === "user" ? "is-selected" : undefined}
            aria-pressed={role === "user"}
            onClick={() => setRole("user")}
          >
            User
          </button>
          <button
            type="button"
            className={role === "admin" ? "is-selected" : undefined}
            aria-pressed={role === "admin"}
            onClick={() => setRole("admin")}
          >
            Admin
          </button>
        </div>
        <span className="field-help">
          A user works in their own studio and library and sees their own spend. An admin can also
          manage people and see spend for everyone — but not anyone else&rsquo;s boards, prompts or
          images.
        </span>
      </div>
    </Modal>
  );
}
